/**
 * Appwrite Client Configuration
 * 
 * Two modes of access:
 * 1. Client SDK — for user authentication (login/logout/session check)
 * 2. REST API with API key — for admin operations (list all users, teams, etc.)
 */
import { Client, Account, Teams } from 'appwrite';

// ===== Client SDK (for authentication) =====
const client = new Client()
  .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
  .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

export const account = new Account(client);
export const teamsClient = new Teams(client);

// ===== Environment config =====
const ENDPOINT = process.env.REACT_APP_APPWRITE_ENDPOINT;
const PROJECT_ID = process.env.REACT_APP_APPWRITE_PROJECT_ID;
const API_KEY = process.env.REACT_APP_APPWRITE_API_KEY;
export const ADMINS_TEAM_ID = process.env.REACT_APP_APPWRITE_ADMINS_TEAM_ID;

// ===== REST API Helper =====

/**
 * Makes an authenticated REST API call to Appwrite using the API key.
 * @param {string} path - API path (may include query string)
 * @param {object} options - { method, body }
 */
export const apiRequest = async (path, options = {}) => {
  const { method = 'GET', body } = options;

  const url = `${ENDPOINT}${path}`;

  const headers = {
    'Content-Type': 'application/json',
    'X-Appwrite-Project': PROJECT_ID,
    'X-Appwrite-Key': API_KEY,
  };

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `API error: ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
};

/**
 * Builds a URL path with optional query params.
 */
const buildPath = (basePath, params = {}) => {
  const parts = [];
  if (params.limit) parts.push(`limit=${params.limit}`);
  if (params.offset) parts.push(`offset=${params.offset}`);
  if (params.search) parts.push(`search=${encodeURIComponent(params.search)}`);
  return parts.length > 0 ? `${basePath}?${parts.join('&')}` : basePath;
};

// =============================================
// USER API functions
// =============================================

/** List all users in the project. */
export const listAllUsers = (params = {}) =>
  apiRequest(buildPath('/users', params));

/** Get a specific user by ID. */
export const getUser = (userId) => apiRequest(`/users/${userId}`);

/** Create a new user. */
export const createUser = (userId, email, password, name) =>
  apiRequest('/users', {
    method: 'POST',
    body: { userId, email, password, name },
  });

/** Update user status (true = active, false = blocked). */
export const updateUserStatus = (userId, status) =>
  apiRequest(`/users/${userId}/status`, {
    method: 'PATCH',
    body: { status },
  });

/** Delete a user. */
export const deleteUser = (userId) =>
  apiRequest(`/users/${userId}`, { method: 'DELETE' });

/** Update user labels. */
export const updateUserLabels = (userId, labels) =>
  apiRequest(`/users/${userId}/labels`, {
    method: 'PUT',
    body: { labels },
  });

// =============================================
// TEAMS API functions
// =============================================

/** List all teams. */
export const listAllTeams = (params = {}) =>
  apiRequest(buildPath('/teams', params));

/** Get a specific team. */
export const getTeam = (teamId) => apiRequest(`/teams/${teamId}`);

/** Create a new team. */
export const createTeam = (teamId, name) =>
  apiRequest('/teams', {
    method: 'POST',
    body: { teamId, name },
  });

/** Delete a team. */
export const deleteTeam = (teamId) =>
  apiRequest(`/teams/${teamId}`, { method: 'DELETE' });

/** List all memberships of a team. */
export const listTeamMemberships = (teamId, params = {}) =>
  apiRequest(buildPath(`/teams/${teamId}/memberships`, params));

/** Add a member to a team by email with roles. */
export const addTeamMember = (teamId, email, roles = []) =>
  apiRequest(`/teams/${teamId}/memberships`, {
    method: 'POST',
    body: { email, roles },
  });

/** Update a team membership's roles. */
export const updateMembershipRoles = (teamId, membershipId, roles) =>
  apiRequest(`/teams/${teamId}/memberships/${membershipId}`, {
    method: 'PATCH',
    body: { roles },
  });

/** Remove a member from a team. */
export const removeTeamMember = (teamId, membershipId) =>
  apiRequest(`/teams/${teamId}/memberships/${membershipId}`, { method: 'DELETE' });

/** Get team preferences. */
export const getTeamPrefs = (teamId) => apiRequest(`/teams/${teamId}/prefs`);

/** Update team preferences. */
export const updateTeamPrefs = (teamId, prefs) =>
  apiRequest(`/teams/${teamId}/prefs`, {
    method: 'PUT',
    body: { prefs },
  });

export default client;
